import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { LDClient, LDUser } from 'launchdarkly-js-client-sdk';
import { firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { readUserAuthFromLocalStorage } from 'src/app/core/http/user-auth';
import { logErrorInSentry } from 'src/app/project/modules/errors/response-error';
import { TUser } from 'src/app/project/modules/user/user.model';
import { EStore } from 'src/app/project/shared/enums/store.enum';
import { environment } from 'src/environments/environment';
import {
  FEATURE_FLAG_API_SDK_KEY_DEVELOPMENT,
  FEATURE_FLAG_API_SDK_KEY_PRODUCTION,
  FEATURE_FLAG_API_SDK_KEY_STAGING,
} from './feature-flag-api-key';
import { getLDClient, setFeatureFlagGetter } from './feature-flag-helpers';

const FEATURE_FLAG_READY_TIMEOUT_MS = 3000;
const ANONYMOUS_USER_KEY = 'anonymous_user';
const E2E_USER_KEY = 'e2e_user';
const E2E_USER_EMAIL_PATTERN = /^playwrighttestautomation\d+@voly\.com$/;

function getUserKey(user: TUser | undefined): string {
  if (!user?.userId) {
    return ANONYMOUS_USER_KEY;
  }

  if (E2E_USER_EMAIL_PATTERN.test(user.email ?? '')) {
    return E2E_USER_KEY;
  }

  return user.userId;
}

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private ldClient: LDClient | undefined;
  private flags: { [key: string]: any } = {};
  private user$: Observable<TUser>;

  private _flagsChange$ = new ReplaySubject<void>(1);
  public flagsChange$ = this._flagsChange$.asObservable();

  private ready: Promise<void> | null = null;
  private userIdentified$ = new ReplaySubject<void>(1);

  constructor(
    private store: Store<{
      user: TUser;
    }>,
  ) {
    setFeatureFlagGetter(this.getFlag.bind(this));

    this.user$ = this.store.pipe(select(EStore.USER));

    this.user$.subscribe(async (user) => {
      const newUser: LDUser = {
        key: getUserKey(user),
        anonymous: !user?.userId,
      };

      await this.updateUser(newUser);

      if (!user?.userId) {
        return;
      }

      this.userIdentified$.next();
    });
  }

  initialize(): Promise<void> {
    if (this.ready) {
      return this.ready;
    }

    let apiKey = FEATURE_FLAG_API_SDK_KEY_DEVELOPMENT;

    if (environment.production) {
      apiKey = FEATURE_FLAG_API_SDK_KEY_PRODUCTION;
    } else if (environment.staging) {
      apiKey = FEATURE_FLAG_API_SDK_KEY_STAGING;
    }

    const user: LDUser = {
      anonymous: true,
      key: ANONYMOUS_USER_KEY,
    };

    this.ready = new Promise((resolve) => {
      this.ldClient = getLDClient().initialize(apiKey, user);

      this.ldClient.on('ready', () => {
        this.flags = this.ldClient?.allFlags();
        this._flagsChange$.next();

        resolve();
      });

      this.ldClient.on('failed', (error) => {
        logErrorInSentry({ message: 'LaunchDarkly initialization failed', error });

        resolve();
      });

      this.ldClient.on('change', (changes) => {
        Object.keys(changes).forEach((flagKey) => {
          this.flags[flagKey] = changes[flagKey].current;
        });

        this._flagsChange$.next();
      });
    });

    return this.ready;
  }

  whenFlagsResolved(): Promise<void> {
    return Promise.race([this.initialize(), this.readyTimeout()]);
  }

  whenReady(): Promise<void> {
    return Promise.race([this.initializeAndIdentify(), this.readyTimeout()]);
  }

  private readyTimeout(): Promise<void> {
    return new Promise<void>((resolve) => {
      setTimeout(resolve, FEATURE_FLAG_READY_TIMEOUT_MS);
    });
  }

  private async initializeAndIdentify(): Promise<void> {
    await this.initialize();
    await this.whenUserIdentified();
  }

  private whenUserIdentified(): Promise<void> {
    if (!readUserAuthFromLocalStorage().token) {
      return Promise.resolve();
    }

    return firstValueFrom(this.userIdentified$);
  }

  getFlag(flagKey: string): boolean {
    return this.flags[flagKey] ?? false;
  }

  updateUser(newUser: LDUser): Promise<void> {
    if (!this.ldClient) {
      return Promise.resolve();
    }

    return this.ldClient.identify(newUser).then(() => {
      this.flags = this.ldClient?.allFlags();
    });
  }
}
